import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  describeCarrierWait,
  isAwaitingCarrier,
  isCarrierWaitPending,
} from "@/lib/coiCarrierWait";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FilterChip } from "@/components/FilterChip";
import { IntercomEventDetailModal } from "@/components/IntercomEventDetailModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  Check,
  CheckSquare,
  ChevronRight,
  ChevronUp,
  Download,
  Eye,
  FileText,
  Flag,
  Inbox,
  Loader2,
  Pencil,
  Plus,
  ScrollText,
  Search,
  Square,
  Timer,
  AlarmClockOff,
  Trash2,
  UserX,
  Webhook,
  X,
} from "lucide-react";
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildCOIRequestsExportUrl,
  useCOIRequest,
  useCOIRequests,
  useCOIRequestsStats,
  useCoiSendEvents,
  useCoiSendMetrics,
  useCreateFeatureFlag,
  useDeleteFeatureFlag,
  useFeatureFlag,
  useFeatureFlags,
  useInsuredsWithoutPolicies,
  useIntercomEvent,
  useIntercomEvents,
  useIntercomEventsStats,
  useUpdateFeatureFlag,
  type COIRequestWithEmail,
  type CoiSendEvent,
  type CoiSendMetrics,
  type IntercomEventListItem,
} from "@/lib/api";
import type { FeatureFlag } from "@shared/schema";
import {
  COI_NOT_SENT_REASON_LABELS,
  COI_NOT_SENT_REASONS,
  COI_REQUEST_SOURCE_LABELS,
  COI_REQUEST_SOURCES,
  INTERCOM_SORT_FIELDS,
  INTERCOM_STATUS_FILTERS,
  formatCoiRequestSentBy,
  type COINotSentReason,
  type COIRequestSortField,
  type COIRequestSource,
  type COISentFilter,
  type IntercomSortField,
  type IntercomStatusFilter,
  type SortDir,
} from "@shared/pagination";
import { useAgencyContext } from "@/lib/agencyContext";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  TABLE_CLASS,
  THEAD_CLASS,
  TH_CLASS,
  TBODY_CLASS,
  TR_CLASS,
  TD_CLASS,
} from "@/lib/table-styles";
import {
  StaleSyncTab,
  FLAG_STALE_SYNC_VIEW,
} from "@/components/system-settings/StaleSyncTab";
import { FLAG_AOR_EXPERIENCE } from "@shared/feature-flags";

// CSR-867 cycle 4 — Display labels are owned by `@shared/pagination` so the
// server's CSV export (`buildCoiRequestsCsv` in `server/routes.ts`) and the
// UI render identical strings. The local consts below keep the existing
// callsite ergonomics (`SOURCE_LABELS[value]` over a `string` key) while
// delegating the data to the shared source of truth.
const SOURCE_LABELS: Record<string, string> = COI_REQUEST_SOURCE_LABELS;
const NOT_SENT_REASON_LABELS: Record<string, string> = COI_NOT_SENT_REASON_LABELS;

type ReasonDisplay = { label: string; tooltip?: string };

// CSR-2100: re-exported so callers (and tests) that reach for these through the
// page keep working; they live in `lib` because the data layer needs them too.
export { describeCarrierWait, isAwaitingCarrier, isCarrierWaitPending };

// COI rows can land in "not sent" terminal states without a `notSentReason`
// (approve-job revert sets `lastApprovalError` only; manual rejection sets
// neither). Derive a meaningful label from the surrounding state instead of
// rendering an empty cell.
//
// CSR-1065: when the row is still `pending_review` with no `notSentReason`,
// the upstream `unmappedFlow` very likely threw and the wrap-and-rewrite
// either hasn't landed yet or — for rows created before this fix — never
// will. Render "Stuck — needs investigation" instead of the prior tautological
// "Pending Review" so operators have a clear actionable signal, and surface
// the linked `intercomEventId` in the tooltip so the audit trail is one
// search away on the Intercom Events page.
//
// CSR-1321: the "Intercom ticket created" sub-branch was removed — the ticket
// id already renders in its own column, so the label was pure duplication.
// Rows created after CSR-1321 always carry a real `notSentReason`
// (`auto_send_disabled` / `carrier_not_in_rollout` / a validation reason) and
// hit the first branch; legacy ticket+no-reason rows fall through to the
// actionable "Stuck — needs investigation" fallback.
// CSR-2100 — the COI service waits on a carrier in two places (asset
// verification, and the parked pre-send refresh). The longest legitimate wait
// is the ~3-minute verification cap plus the 6-minute park, so past ten minutes
// nothing is coming and the row is stranded, not working.
//
// Both readings matter, which is why this is one function and not a label: for
// the first ten minutes it must NOT say "stuck", because nobody has to act; and
// after them it must, because someone does. The elapsed time is shown either
// way — it is what lets a person tell the two apart at a glance.
export function deriveReasonDisplay(
  request: COIRequestWithEmail,
  now: number = Date.now(),
): ReasonDisplay | null {
  if (request.notSentReason) {
    return {
      label:
        NOT_SENT_REASON_LABELS[request.notSentReason] ?? request.notSentReason,
    };
  }
  if (request.lastApprovalError) {
    return { label: "Send Failed", tooltip: request.lastApprovalError };
  }
  if (request.status === "rejected") return { label: "Manually Rejected" };
  if (request.status === "pending_review") {
    return {
      label: "Stuck — needs investigation",
      tooltip: request.intercomEventId
        ? `View Intercom Event ${request.intercomEventId}`
        : undefined,
    };
  }
  if (isAwaitingCarrier(request.status)) {
    return describeCarrierWait(request.updatedAt, now);
  }
  if (request.status === "approved") return { label: "Awaiting Send" };
  if (request.status === "awaiting_customer_reply") {
    return {
      label: "Awaiting customer reply",
      tooltip: request.intercomEventId
        ? `View Intercom Event ${request.intercomEventId}`
        : undefined,
    };
  }
  // CSR-1441 fallback: `unhandled` rows always carry a `notSentReason` (first
  // branch), so this only renders if a row somehow lost its reason.
  if (request.status === "unhandled") {
    return {
      label: "Unhandled",
      tooltip: request.intercomEventId
        ? `View Intercom Event ${request.intercomEventId}`
        : undefined,
    };
  }
  return null;
}

function isSent(status: string): boolean {
  return status === "completed" || status === "auto_sent";
}

// CSR-1759 — display-only COI Term selection from the aplite form. Both
// columns NULL means the form never showed the section (pre-CSR-1759 client
// or insured not in the renewal period) → "--" per the stable-layout rule.
export function formatCoiTerm(
  request: Pick<COIRequestWithEmail, "coiTermCurrent" | "coiTermRenewal">,
): string {
  const { coiTermCurrent, coiTermRenewal } = request;
  if (coiTermCurrent == null && coiTermRenewal == null) return "--";
  const parts: string[] = [];
  if (coiTermCurrent) parts.push("Current term");
  if (coiTermRenewal) parts.push("Renewal term");
  return parts.length > 0 ? parts.join(", ") : "None selected";
}

// CSR-937 — when no `certificate_holders` row was linked to a COI request,
// fall back to the name OpenAI extracted from the inbound email so the
// detail dialog stops rendering "—" on sent rows where the holder data
// was clearly present in `openai_response.output_parsed`.
export function resolveCertificateHolderDisplayName(
  request: Pick<COIRequestWithEmail, "certificateHolderName" | "openaiData">,
): string {
  const linked = request.certificateHolderName?.trim();
  if (linked) return linked;

  const openai = request.openaiData;
  if (openai && typeof openai === "object" && "output_parsed" in openai) {
    const parsed = (openai as { output_parsed: unknown }).output_parsed;
    if (parsed && typeof parsed === "object" && "certificate_holder_name" in parsed) {
      const name = (parsed as { certificate_holder_name: unknown })
        .certificate_holder_name;
      if (typeof name === "string") {
        const trimmed = name.trim();
        if (trimmed) return trimmed;
      }
    }
  }

  return "—";
}

const INTERCOM_CONVERSATION_URL_BASE =
  "https://app.intercom.com/a/apps/l4x2o4hr/conversations";
function intercomConversationUrl(ticketId: string) {
  return `${INTERCOM_CONVERSATION_URL_BASE}/${encodeURIComponent(ticketId)}`;
}

// CSR-936 — list endpoint returns naïve Postgres strings (parsed as local),
// detail endpoint returns ISO with `Z` (parsed as UTC). Strip the trailing
// timezone suffix so both encodings render the same wall-clock time, matching
// the table's existing rendering (CSR-673 precedent).
function formatDate(date: string | Date) {
  const parsed =
    typeof date === "string"
      ? new Date(date.replace(/(Z|[+-]\d{2}:?\d{2})$/, ""))
      : new Date(date);
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const COI_REQUESTS_PAGE_SIZE = 25;

function COIRequestsTab() {
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;

  const [page, setPage] = useState(1);
  const [qInput, setQInput] = useState("");
  const debouncedQ = useDebouncedValue(qInput, 250);
  const [sourceFilter, setSourceFilter] = useState<COIRequestSource | "all">("all");
  const [sentFilter, setSentFilter] = useState<COISentFilter | "all">("all");
  const [notSentReasons, setNotSentReasons] = useState<Set<COINotSentReason>>(
    new Set(),
  );
  const [notSentOpen, setNotSentOpen] = useState(false);
  // CSR-867 cycle 5 — `Created At` date range filter (PM Ana, comment
  // f50e3519). Both inputs are independently optional ISO `yyyy-mm-dd`
  // strings (the native value emitted by `<input type="date">`). The
  // client clamps an inverted range below before wiring it to the
  // query so the table never receives a transient zero-row state from
  // the user's typing.
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [sortColumn, setSortColumn] = useState<COIRequestSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDir>("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // CSR-867: preview the COI PDF inline. Mirrors the pattern from
  // `client/src/components/insured/tabs/COIsTab.tsx`.
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const notSentReasonsKey = useMemo(
    () => Array.from(notSentReasons).sort().join(","),
    [notSentReasons],
  );

  // Clamp an inverted range — if the user types `from > to`, swap so the
  // query stays valid. Visible inputs keep what the user typed; only the
  // values passed to the hook + CSV export are reordered.
  const { effectiveCreatedAtFrom, effectiveCreatedAtTo, dateRangeInverted } =
    useMemo(() => {
      if (createdAtFrom && createdAtTo && createdAtFrom > createdAtTo) {
        return {
          effectiveCreatedAtFrom: createdAtTo,
          effectiveCreatedAtTo: createdAtFrom,
          dateRangeInverted: true,
        };
      }
      return {
        effectiveCreatedAtFrom: createdAtFrom,
        effectiveCreatedAtTo: createdAtTo,
        dateRangeInverted: false,
      };
    }, [createdAtFrom, createdAtTo]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedQ,
    sourceFilter,
    sentFilter,
    notSentReasonsKey,
    effectiveCreatedAtFrom,
    effectiveCreatedAtTo,
    sortColumn,
    sortDirection,
    agencyId,
  ]);

  const { data, isLoading, isPlaceholderData } = useCOIRequests({
    agencyId,
    page,
    pageSize: COI_REQUESTS_PAGE_SIZE,
    q: debouncedQ || undefined,
    source: sourceFilter === "all" ? undefined : sourceFilter,
    sentFilter: sentFilter === "all" ? undefined : sentFilter,
    notSentReasons: notSentReasons.size > 0 ? Array.from(notSentReasons) : undefined,
    createdAtFrom: effectiveCreatedAtFrom || undefined,
    createdAtTo: effectiveCreatedAtTo || undefined,
    sort: sortColumn,
    sortDir: sortDirection,
  });

  // The counts move when a carrier wait ends and this query cannot see that
  // happen, so it follows the same condition the list polls on.
  const hasCarrierWait = (data?.data ?? []).some((r) =>
    isAwaitingCarrier(r.status),
  );
  // Polling stopping is not the same as the counts being right: the last stats
  // fetch can predate the transition by its whole interval, so the edge asks
  // for one more.
  const listQueryClient = useQueryClient();
  const wasWaiting = useRef(false);
  useEffect(() => {
    if (wasWaiting.current && !hasCarrierWait) {
      listQueryClient.invalidateQueries({
        queryKey: ["coi-requests-stats"],
      });
    }
    wasWaiting.current = hasCarrierWait;
  }, [hasCarrierWait, listQueryClient]);
  const { data: stats } = useCOIRequestsStats(agencyId, {
    refreshWhileWaiting: hasCarrierWait,
  });

  const requests = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / COI_REQUESTS_PAGE_SIZE));
  const showFooter = total > COI_REQUESTS_PAGE_SIZE;

  const sentPct =
    stats && stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0;

  const handleSort = (col: COIRequestSortField) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortColumn(col);
      setSortDirection("desc");
    }
  };

  const sortIcon = (col: COIRequestSortField) => {
    if (sortColumn !== col)
      return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5" />
    );
  };

  // CSR-867: trigger a CSV download for the current filtered dataset.
  // We navigate to the URL rather than fetching a blob so the browser
  // honors the server's `Content-Disposition: attachment` header and
  // saves the file directly. `isAuthenticated` middleware uses the
  // existing session cookie, which is included on top-level navigation.
  const handleExportCsv = () => {
    const url = buildCOIRequestsExportUrl({
      agencyId,
      q: debouncedQ || undefined,
      source: sourceFilter === "all" ? undefined : sourceFilter,
      sentFilter: sentFilter === "all" ? undefined : sentFilter,
      notSentReasons:
        notSentReasons.size > 0 ? Array.from(notSentReasons) : undefined,
      createdAtFrom: effectiveCreatedAtFrom || undefined,
      createdAtTo: effectiveCreatedAtTo || undefined,
      sort: sortColumn,
      sortDir: sortDirection,
    });
    window.location.assign(url);
  };

  return (
    <div className="space-y-6">
      {/* CSR-867 cycle 5 — header row. The Export CSV button moves up here
          per Ana's mockup (comment f50e3519): aligned right with the
          heading, above the stats cards. `justify-between` pushes it to
          the right edge regardless of the heading's width. */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            COI Requests
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            COI requests and generations, sorted by most recent.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          data-testid="button-export-csv"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Requests" value={stats?.total ?? 0} />
        <StatCard
          label="Sent"
          value={`${sentPct}%`}
          className="text-emerald-600"
        />
        <StatCard
          label="Not Sent"
          value={stats?.notSent ?? 0}
          className="text-rose-500"
        />
        <StatCard label="Request Sources" value={stats?.sources ?? 0} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer name, email, insured, or request id"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="pl-9 rounded-lg bg-white"
            data-testid="input-search-coi-requests"
          />
        </div>

        <Select
          value={sourceFilter}
          onValueChange={(v) => setSourceFilter(v as COIRequestSource | "all")}
        >
          <SelectTrigger className="w-[160px] bg-white">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {COI_REQUEST_SOURCES.map((value) => (
              <SelectItem key={value} value={value}>
                {SOURCE_LABELS[value] ?? value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sentFilter}
          onValueChange={(v) => setSentFilter(v as COISentFilter | "all")}
        >
          <SelectTrigger className="w-[160px] bg-white">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="not_sent">Not Sent</SelectItem>
          </SelectContent>
        </Select>

        <Popover open={notSentOpen} onOpenChange={setNotSentOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 h-9 px-3 border rounded-md bg-white text-sm cursor-pointer hover:bg-muted/50 transition-colors">
              <span className="text-muted-foreground font-medium uppercase text-xs">
                Not Sent Reason:
              </span>
              <span>
                {notSentReasons.size === 0
                  ? "All not sent reasons"
                  : `${notSentReasons.size} selected`}
              </span>
              <ChevronUp
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  !notSentOpen && "rotate-180",
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[320px] p-0">
            <div className="max-h-[320px] overflow-y-auto">
              <label className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer border-b">
                <Checkbox
                  checked={notSentReasons.size === 0}
                  onCheckedChange={() => setNotSentReasons(new Set())}
                />
                <span className="font-medium">All</span>
              </label>
              {COI_NOT_SENT_REASONS.map((value) => (
                <label
                  key={value}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={notSentReasons.has(value)}
                    onCheckedChange={(checked) => {
                      setNotSentReasons((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(value);
                        else next.delete(value);
                        return next;
                      });
                    }}
                  />
                  <span>{NOT_SENT_REASON_LABELS[value] ?? value}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* CSR-867 cycle 5 — `Created At` date range filter (Ana, comment
            f50e3519). Two `<input type="date">` controls grouped in a
            single bordered container with an arrow separator, matching
            the mockup. Both inputs are independently optional. */}
        <div
          className="flex items-center h-9 px-2 border rounded-md bg-white text-sm gap-1 ml-auto"
          data-testid="filter-created-at-range"
        >
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <input
            type="date"
            value={createdAtFrom}
            onChange={(e) => setCreatedAtFrom(e.target.value)}
            className="bg-transparent outline-none text-sm w-[125px]"
            aria-label="Created At from"
            data-testid="input-created-at-from"
          />
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <input
            type="date"
            value={createdAtTo}
            onChange={(e) => setCreatedAtTo(e.target.value)}
            className="bg-transparent outline-none text-sm w-[125px]"
            aria-label="Created At to"
            data-testid="input-created-at-to"
          />
          {(createdAtFrom || createdAtTo) && (
            <button
              type="button"
              onClick={() => {
                setCreatedAtFrom("");
                setCreatedAtTo("");
              }}
              className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear Created At range"
              data-testid="button-clear-created-at"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {dateRangeInverted && (
        <p
          className="text-xs text-muted-foreground -mt-3"
          data-testid="text-date-range-inverted"
        >
          Start date is after end date — showing results for the swapped range.
        </p>
      )}

      <Card className="border-border shadow-none overflow-hidden rounded-xl">
        <div className="overflow-auto">
          <table className={TABLE_CLASS}>
            <thead className={THEAD_CLASS}>
              <tr>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("createdAt")}
                  data-testid="th-created-at"
                >
                  <span className="inline-flex items-center gap-1">
                    Created At
                    {sortIcon("createdAt")}
                  </span>
                </th>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("customerName")}
                  data-testid="th-customer-name"
                >
                  <span className="inline-flex items-center gap-1">
                    Customer Name
                    {sortIcon("customerName")}
                  </span>
                </th>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("customerEmail")}
                  data-testid="th-customer-email"
                >
                  <span className="inline-flex items-center gap-1">
                    COI Requester
                    {sortIcon("customerEmail")}
                  </span>
                </th>
                <th className={TH_CLASS}>Sent By</th>
                <th className={TH_CLASS}>COI Was Sent</th>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("source")}
                  data-testid="th-source"
                >
                  <span className="inline-flex items-center gap-1">
                    Request Source
                    {sortIcon("source")}
                  </span>
                </th>
                <th className={TH_CLASS}>Actions</th>
              </tr>
            </thead>
            <tbody className={TBODY_CLASS}>
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-16 text-center text-muted-foreground"
                  >
                    <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-base font-medium">No COI requests</p>
                    <p className="text-sm mt-1">
                      COI requests from inbound emails or Intercom will appear
                      here.
                    </p>
                  </td>
                </tr>
              ) : (
                requests.map((req) => (
                  <COIRequestRow
                    key={req.id}
                    request={req}
                    onClick={() => {
                      // CSR-867: orphan (`source: "ams"`) rows have no
                      // `coi_requests` row to look up — the inspector
                      // dialog reads from `/api/coi-requests/:id` which
                      // would 404. The eye icon provides the PDF preview
                      // (the only artifact those rows have anyway).
                      if (req.source === "ams") return;
                      setSelectedId(req.id);
                    }}
                    onPreview={(docId) => setPreviewDocId(docId)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {showFooter && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              {((page - 1) * COI_REQUESTS_PAGE_SIZE + 1).toLocaleString()}&ndash;
              {Math.min(page * COI_REQUESTS_PAGE_SIZE, total).toLocaleString()} of{" "}
              {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1 || isPlaceholderData}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isPlaceholderData}
              >
                Previous
              </Button>
              <span className="text-sm px-3">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isPlaceholderData}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || isPlaceholderData}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>

      <COIRequestInspector
        requestId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />

      {previewDocId && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setPreviewDocId(null);
          }}
        >
          <DialogContent
            className="max-w-4xl w-[90vw] h-[85vh] flex flex-col p-0"
            aria-describedby={undefined}
          >
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
              <DialogTitle className="flex items-center gap-2 text-base">
                <FileText className="w-4 h-4" />
                Certificate of Insurance
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 px-6 pb-6">
              <iframe
                src={`/api/documents/${previewDocId}/view`}
                className="w-full h-full rounded-lg border"
                title="COI Preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export function COIRequestRow({
  request,
  onClick,
  onPreview,
}: {
  request: COIRequestWithEmail;
  onClick: () => void;
  onPreview: (documentId: string) => void;
}) {
  const sent = isSent(request.status);
  const email =
    request.customerEmail || request.inboundEmail?.fromEmail || "—";
  // CSR-867: AMS rows have no `coi_requests` backing row, so opening the
  // detail inspector would 404. Drop the cursor affordance on those rows.
  const clickable = request.source !== "ams";
  const documentId = request.documentId ?? null;

  return (
    <tr
      className={cn(TR_CLASS, clickable && "cursor-pointer")}
      onClick={clickable ? onClick : undefined}
    >
      <td className={cn(TD_CLASS, "whitespace-nowrap")}>
        <span className="text-sm text-muted-foreground">
          {formatDate(request.createdAt)}
        </span>
      </td>
      <td className={TD_CLASS}>
        <span className="font-medium">{request.customerName || "—"}</span>
      </td>
      <td className={TD_CLASS}>
        <span className="text-sm text-muted-foreground">{email}</span>
      </td>
      <td className={TD_CLASS}>
        <span className="text-sm">
          {formatCoiRequestSentBy({
            issuedByName: request.issuedByName ?? null,
            source: request.source,
            coiWasSent: sent,
          }) ?? "—"}
        </span>
      </td>
      <td className={TD_CLASS}>
        {sent ? (
          <Check
            className="w-4 h-4 text-emerald-500"
            aria-label="Sent"
          />
        ) : isCarrierWaitPending(request) ? (
          // CSR-2100: a red cross means "this did not happen". A request in a
          // carrier wait has not happened YET — true whether the wait is
          // healthy or has gone on too long. Whether it is still COMING is a
          // different question, and the reason column beside this one already
          // answers it, with the elapsed time and the ten-minute rule.
          //
          // A recorded failure wins, though: that column gives `notSentReason`
          // and `lastApprovalError` precedence, and an amber "waiting" beside
          // "Send Failed" is the row contradicting itself.
          //
          // Keeping that judgement out of here is deliberate. Tying the icon to
          // elapsed time means a live clock in the table, and a clock whose
          // input is derived from its own output can freeze in a state that
          // prevents it restarting. Not a spinner either: nothing is happening
          // in this tab, and a page of them would say otherwise.
          <Timer
            className="w-4 h-4 text-amber-500"
            aria-label="Waiting on carrier"
          />
        ) : (
          <X className="w-4 h-4 text-rose-500" aria-label="Not sent" />
        )}
      </td>
      <td className={TD_CLASS}>
        <span className="text-sm">
          {SOURCE_LABELS[request.source] || request.source}
        </span>
      </td>
      <td className={TD_CLASS}>
        {/*
          CSR-867 follow-up: per the approved prototype, the Eye icon is
          rendered ONLY for rows where the COI was actually sent (status
          ∈ {completed, auto_sent}). The previous gating on `documentId`
          alone left a disabled icon visible for unsent rows, which the
          spec explicitly disallows.
        */}
        {sent &&
          (documentId ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(documentId);
              }}
              data-testid="button-preview-coi"
              aria-label="View COI"
            >
              <Eye className="w-4 h-4 text-muted-foreground" />
            </Button>
          ) : (
            // Sent rows with no PDF on file: keep a disabled affordance so
            // the column stays aligned, and explain via tooltip. The
            // `<span tabIndex={0}>` wrapper is the standard workaround
            // for showing a tooltip on a natively-disabled button —
            // browsers swallow mouse events on `disabled` buttons, so
            // hover is captured by the span (button is pointer-events:none).
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} aria-disabled="true" className="inline-block">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 pointer-events-none"
                    disabled
                    data-testid="button-preview-coi-disabled"
                    aria-label="View COI (unavailable)"
                  >
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>COI was sent but no PDF is on file</TooltipContent>
            </Tooltip>
          ))}
      </td>
    </tr>
  );
}

function StatCard({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className={`text-2xl font-bold ${className || "text-foreground"}`}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

const REQUESTER_LABELS: Record<string, string> = {
  assure_assist: "Assure Assist",
  registry_monitoring: "Registry Monitoring",
  highway: "Highway",
};

/** CSR-2100 — a clock that ticks only while a displayed request is in a
 *  carrier wait.
 *
 *  Two different things go stale here and each needs its own mechanism. The
 *  STATUS is refreshed by `useCOIRequest`'s polling, which runs until the
 *  server ends the wait. The ELAPSED TIME is not: `describeCarrierWait` reads
 *  the clock, and React does not re-render because a minute went by — so an
 *  inspector opened at two minutes would sit reading "Waiting on carrier
 *  (2 min)" long after that stopped being true, never reaching the actionable
 *  label. Half-minute ticks keep the displayed minute at most a minute stale. */
function useMinuteClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function COIRequestInspector({
  requestId,
  onOpenChange,
}: {
  requestId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: request, isLoading } = useCOIRequest(requestId ?? "");
  const { data: intercomEvent } = useIntercomEvent(
    request?.intercomEventId ?? null,
  );
  // Only while this request's label depends on elapsed time.
  const now = useMinuteClock(isAwaitingCarrier(request?.status ?? ""));
  // CSR-2100: the detail poll refreshes THIS request; the table behind the
  // dialog and the counters above it keep whatever they last fetched. So the
  // moment the wait ends, tell them — otherwise the row the user is looking at
  // says "sent" while the list still files it under Not Sent, and the totals
  // disagree with both. Fires on the transition out of the wait, once.
  const queryClient = useQueryClient();
  const wasAwaiting = useRef(false);
  useEffect(() => {
    const awaiting = isAwaitingCarrier(request?.status ?? "");
    if (wasAwaiting.current && !awaiting && request) {
      queryClient.invalidateQueries({ queryKey: ["coi-requests"] });
      queryClient.invalidateQueries({ queryKey: ["coi-requests-stats"] });
    }
    wasAwaiting.current = awaiting;
  }, [request?.status, request, queryClient]);
  const open = !!requestId;

  const intercomEventDisplay = intercomEvent
    ? `IntercomEvent(item_type='${intercomEvent.itemType ?? "—"}', id='${intercomEvent.itemId ?? intercomEvent.id}')`
    : request?.intercomEventId
      ? request.intercomEventId
      : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              COI Request{request?.customerName ? ` — ${request.customerName}` : ""}
            </DialogTitle>
            <DialogDescription>
              Read-only view of a COI request and the data captured for it.
            </DialogDescription>
          </DialogHeader>

          {isLoading || !request ? (
            <div className="mt-6 space-y-3">
              <div className="h-6 bg-muted animate-pulse rounded" />
              <div className="h-6 bg-muted animate-pulse rounded" />
              <div className="h-64 bg-muted animate-pulse rounded" />
            </div>
          ) : (
            <div className="mt-6 space-y-8 min-w-0">
              <DetailSection title="Source">
                <DetailField
                  label="Request Source"
                  value={SOURCE_LABELS[request.source] ?? request.source}
                  helper="Where this COI request comes from"
                />
                {intercomEventDisplay && (
                  <DetailField
                    label="Intercom event"
                    value={intercomEventDisplay}
                    mono
                    helper="The Intercom event that triggered this COI request"
                  />
                )}
                {request.forwardedRecovery && (
                  <div className="grid grid-cols-3 gap-3 border-b pb-2">
                    <dt className="text-xs font-medium text-muted-foreground">
                      Provenance
                    </dt>
                    <dd className="col-span-2 space-y-2">
                      <Badge
                        className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                        data-testid="badge-forwarded-recovery"
                      >
                        {request.forwardedRecovery.source === "gmail"
                          ? "Recovered from Gmail (forwarded email)"
                          : "Recovered from forwarded email"}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {request.forwardedRecovery.source === "gmail"
                          ? "The original COI request was recovered from the support@logrock.com Gmail mailbox by message ID (CSR-1581 Tier-2 fallback)."
                          : "The original COI request was recovered from a forwarded Intercom email."}
                      </p>
                    </dd>
                  </div>
                )}
                {request.forwardedRecovery && (
                  <DetailField
                    label="Recovered message ID"
                    value={request.forwardedRecovery.messageId}
                    mono
                  />
                )}
                {request.forwardedRecovery && (
                  <DetailField
                    label="Recovered at"
                    value={formatDate(request.forwardedRecovery.recoveredAt)}
                  />
                )}
              </DetailSection>

              <DetailSection title="Customer">
                <DetailField label="Customer name" value={request.customerName ?? "—"} />
                <DetailField label="Email" value={request.customerEmail ?? "—"} />
                <DetailField label="Subject" value={request.subject ?? "—"} />
                <DetailField label="Description" value={request.messageBody ?? "—"} />
                {request.requester && (
                  <DetailField
                    label="Requester"
                    value={REQUESTER_LABELS[request.requester] ?? request.requester}
                    helper="Determines which extractor is used to parse the request data"
                  />
                )}
              </DetailSection>

              <DetailSection title="Status">
                <BooleanField label="COI was sent" checked={isSent(request.status)} />
                {(() => {
                  const reason = deriveReasonDisplay(request, now);
                  if (!reason) return null;
                  return (
                    <DetailField
                      label="Not sent reason"
                      value={reason.label}
                      helper={reason.tooltip ?? undefined}
                    />
                  );
                })()}
              </DetailSection>

              {request.openaiData != null && (
                <DetailSection title="OpenAI Data" subtitle="JSON data from an OpenAI response">
                  <pre className="rounded-lg bg-muted p-4 text-xs font-mono overflow-auto max-h-[480px] max-w-full whitespace-pre">
                    {JSON.stringify(request.openaiData, null, 2)}
                  </pre>
                </DetailSection>
              )}

              <DetailSection title="Certificate Holder">
                <DetailField
                  label="Certificate Holder"
                  value={resolveCertificateHolderDisplayName(request)}
                  helper="The entity that will be listed on the certificate"
                />
                {request.certificateHolderSource === "customer_reply" && (
                  <div className="grid grid-cols-3 gap-3 border-b pb-2">
                    <dt className="text-xs font-medium text-muted-foreground">
                      Provenance
                    </dt>
                    <dd className="col-span-2 space-y-2">
                      <Badge
                        className="bg-sky-100 text-sky-700 hover:bg-sky-100"
                        data-testid="badge-holder-from-reply"
                      >
                        Holder info from customer reply
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        The certificate holder was supplied by the customer's
                        reply to the incomplete-holder follow-up (CSR-1420) —
                        the OpenAI Data above shows the original request, which
                        had no holder.
                      </p>
                    </dd>
                  </div>
                )}
                <BooleanField
                  label="Send copy to insured"
                  checked={request.sendCopyToInsured}
                  helper="Whether to send a copy of the COI to the insured"
                />
                <BooleanField
                  label="Include all vehicles"
                  checked={request.includeAllVehicles}
                  helper="Whether to include all vehicles on the certificate"
                />
                <BooleanField
                  label="Include all power units"
                  checked={request.includeAllPowerUnits}
                  helper="Whether to include all power units on the certificate"
                />
                <BooleanField
                  label="Include all trailers"
                  checked={request.includeAllTrailers}
                  helper="Whether to include all trailers on the certificate"
                />
                <BooleanField
                  label="Include all drivers"
                  checked={request.includeAllDrivers}
                  helper="Whether to include all drivers on the certificate"
                />
                <DetailField
                  label="COI Term"
                  value={formatCoiTerm(request)}
                  helper="Term(s) selected on the COI Form when the insured is in the renewal period (CSR-1759)"
                />
              </DetailSection>

              {request.reviewNotes && (
                <DetailSection title="Review">
                  <DetailField label="Review notes" value={request.reviewNotes} />
                </DetailSection>
              )}

              <DetailSection title="Metadata">
                <DetailField label="UUID" value={request.id} mono />
                <DetailField label="Created at" value={formatDate(request.createdAt)} />
                <DetailField label="Updated at" value={formatDate(request.updatedAt)} />
                <DetailField
                  label="Deleted at"
                  value="—"
                  helper="Defines if the entity has been deleted or not and when"
                />
                {request.intercomTicketId && (
                  <DetailLinkField
                    label="Intercom ticket"
                    href={intercomConversationUrl(request.intercomTicketId)}
                    text={request.intercomTicketId}
                    helper="Opens the Intercom conversation in a new tab"
                  />
                )}
              </DetailSection>
            </div>
          )}

        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
        {title}
      </p>
      {subtitle && (
        <p className="text-xs text-muted-foreground mb-2">{subtitle}</p>
      )}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function DetailField({
  label,
  value,
  helper,
  mono,
}: {
  label: string;
  value: string;
  helper?: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b pb-2">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2">
        <div className={cn("text-sm break-words", mono && "font-mono text-xs")}>
          {value}
        </div>
        {helper && (
          <p className="text-xs text-muted-foreground mt-1">{helper}</p>
        )}
      </dd>
    </div>
  );
}

function DetailLinkField({
  label,
  href,
  text,
  helper,
}: {
  label: string;
  href: string;
  text: string;
  helper?: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b pb-2">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm break-words text-primary underline"
          data-testid="link-intercom-ticket"
        >
          {text}
        </a>
        {helper && (
          <p className="text-xs text-muted-foreground mt-1">{helper}</p>
        )}
      </dd>
    </div>
  );
}

function BooleanField({
  label,
  checked,
  helper,
}: {
  label: string;
  checked: boolean;
  helper?: string;
}) {
  const Icon = checked ? CheckSquare : Square;
  return (
    <div className="grid grid-cols-3 gap-3 border-b pb-2">
      <dt className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon
          className={cn(
            "h-4 w-4",
            checked ? "text-primary" : "text-muted-foreground/60",
          )}
        />
        {label}
      </dt>
      <dd className="col-span-2">
        {helper && (
          <p className="text-xs text-muted-foreground">{helper}</p>
        )}
      </dd>
    </div>
  );
}

const INTERCOM_STATUS_LABELS: Record<IntercomStatusFilter, string> = {
  received: "Received",
  processing: "Processing",
  processed: "Processed",
  skipped: "Skipped",
  failed: "Failed",
};

const INTERCOM_STATUS_BADGE_CLASS: Record<IntercomStatusFilter, string> = {
  received: "bg-slate-100 text-slate-700",
  processing: "bg-amber-100 text-amber-700",
  processed: "bg-emerald-100 text-emerald-700",
  skipped: "bg-zinc-100 text-zinc-600",
  failed: "bg-rose-100 text-rose-700",
};

const INTERCOM_PAGE_SIZE = 25;

function IntercomEventsTab() {
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<IntercomStatusFilter | null>(
    null,
  );
  const [qInput, setQInput] = useState("");
  const debouncedQ = useDebouncedValue(qInput, 250);
  const [sortColumn, setSortColumn] = useState<IntercomSortField>("receivedAt");
  const [sortDirection, setSortDirection] = useState<SortDir>("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, statusFilter, sortColumn, sortDirection, agencyId]);

  const { data, isLoading, isPlaceholderData } = useIntercomEvents({
    agencyId,
    page,
    pageSize: INTERCOM_PAGE_SIZE,
    q: debouncedQ || undefined,
    status: statusFilter || undefined,
    sort: sortColumn,
    sortDir: sortDirection,
  });
  const { data: stats } = useIntercomEventsStats(agencyId);

  const events = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / INTERCOM_PAGE_SIZE));
  const showFooter = total > INTERCOM_PAGE_SIZE;

  const handleSort = (col: IntercomSortField) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortColumn(col);
      setSortDirection("desc");
    }
  };

  const sortIcon = (col: IntercomSortField) => {
    if (sortColumn !== col)
      return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5" />
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Webhook className="h-4 w-4" />
          Intercom Events
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Webhook deliveries from Intercom, ordered by most recent. Click a row
          to inspect the raw payload.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {INTERCOM_STATUS_FILTERS.map((s) => (
          <FilterChip
            key={s}
            label={INTERCOM_STATUS_LABELS[s]}
            count={stats?.[s].count ?? 0}
            selected={statusFilter === s}
            onClick={() => setStatusFilter(statusFilter === s ? null : s)}
            accent="green"
            data-testid={`chip-${s}`}
          />
        ))}
        <div className="relative ml-auto w-full max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Search by notification id, item id, contact id, or topic"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="pl-9"
            data-testid="input-search-intercom-events"
          />
        </div>
      </div>

      <Card className="border-border shadow-none overflow-hidden rounded-xl">
        <div className="overflow-auto">
          <table className={TABLE_CLASS}>
            <thead className={THEAD_CLASS}>
              <tr>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("receivedAt")}
                  data-testid="th-received-at"
                >
                  <span className="inline-flex items-center gap-1">
                    Received At
                    {sortIcon("receivedAt")}
                  </span>
                </th>
                <th className={TH_CLASS}>Item Type</th>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("topic")}
                  data-testid="th-topic"
                >
                  <span className="inline-flex items-center gap-1">
                    Topic
                    {sortIcon("topic")}
                  </span>
                </th>
                <th className={TH_CLASS}>Item ID</th>
                <th className={TH_CLASS}>Contact ID</th>
                <th
                  className={cn(
                    TH_CLASS,
                    "cursor-pointer select-none hover:text-foreground transition-colors",
                  )}
                  onClick={() => handleSort("processingStatus")}
                  data-testid="th-status"
                >
                  <span className="inline-flex items-center gap-1">
                    Status
                    {sortIcon("processingStatus")}
                  </span>
                </th>
                <th className={TH_CLASS}>Error Message</th>
                <th className={cn(TH_CLASS, "w-12")} />
              </tr>
            </thead>
            <tbody className={TBODY_CLASS}>
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-12 text-center text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-16 text-center text-muted-foreground"
                  >
                    <Webhook className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-base font-medium">No Intercom events</p>
                    <p className="text-sm mt-1">
                      Webhook deliveries from Intercom will appear here.
                    </p>
                  </td>
                </tr>
              ) : (
                events.map((e: IntercomEventListItem) => (
                  <tr
                    key={e.id}
                    className={TR_CLASS}
                    onClick={() => setSelectedId(e.id)}
                    data-testid={`row-intercom-event-${e.id}`}
                  >
                    <td
                      className={cn(
                        TD_CLASS,
                        "whitespace-nowrap text-muted-foreground",
                      )}
                    >
                      {formatDate(e.receivedAt)}
                    </td>
                    <td className={TD_CLASS}>{e.itemType ?? "—"}</td>
                    <td className={TD_CLASS}>{e.topic ?? "—"}</td>
                    <td
                      className={cn(
                        TD_CLASS,
                        "text-xs font-mono text-muted-foreground",
                      )}
                    >
                      {e.itemId ?? "—"}
                    </td>
                    <td
                      className={cn(
                        TD_CLASS,
                        "text-xs font-mono text-muted-foreground",
                      )}
                    >
                      {e.contactId ?? "—"}
                    </td>
                    <td className={TD_CLASS}>
                      <span
                        className={cn(
                          "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                          INTERCOM_STATUS_BADGE_CLASS[
                            e.processingStatus as IntercomStatusFilter
                          ] ?? "bg-slate-100 text-slate-700",
                        )}
                      >
                        {INTERCOM_STATUS_LABELS[
                          e.processingStatus as IntercomStatusFilter
                        ] ?? e.processingStatus}
                      </span>
                    </td>
                    <td
                      className={cn(
                        TD_CLASS,
                        "max-w-xs truncate text-xs",
                      )}
                    >
                      {e.errorMessage ?? "—"}
                    </td>
                    <td className={TD_CLASS}>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {showFooter && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              {((page - 1) * INTERCOM_PAGE_SIZE + 1).toLocaleString()}&ndash;
              {Math.min(page * INTERCOM_PAGE_SIZE, total).toLocaleString()} of{" "}
              {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1 || isPlaceholderData}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isPlaceholderData}
              >
                Previous
              </Button>
              <span className="text-sm px-3">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isPlaceholderData}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || isPlaceholderData}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>

      <IntercomEventDetailModal
        eventId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}

// ============================================================
// COI Send Times Tab (CSR-1349)
// ============================================================

const COI_SEND_TIME_WINDOWS = [7, 30, 90] as const;

const COI_SEND_GROUPS: {
  key: keyof CoiSendMetrics["groups"];
  label: string;
}[] = [
  { key: "manual", label: "Manual COI Send" },
  { key: "coi_form", label: "COI Form (Automated)" },
  { key: "intercom_webhook", label: "Intercom Webhook (Automated)" },
  { key: "combined", label: "All Combined" },
];

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "--";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

const COI_SEND_EVENT_SERIES: { key: string; label: string; color: string }[] = [
  { key: "manual", label: "Manual COI Send", color: "hsl(var(--chart-1))" },
  { key: "coi_form", label: "COI Form (Automated)", color: "hsl(var(--chart-2))" },
  {
    key: "intercom_webhook",
    label: "Intercom Webhook (Automated)",
    color: "hsl(var(--chart-3))",
  },
];

type CoiSendEventPoint = CoiSendEvent & { sentAtMs: number };

function formatSentAtTick(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function CoiSendEventTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: CoiSendEventPoint }>;
}) {
  if (!active || !payload?.length) return null;
  const event = payload[0].payload;
  const originLabel =
    COI_SEND_EVENT_SERIES.find((s) => s.key === event.sendOrigin)?.label ??
    event.sendOrigin;
  return (
    <div className="rounded-lg border bg-card p-3 text-xs shadow-md space-y-1">
      <p className="font-semibold">{event.certificateHolderName}</p>
      <p className="text-muted-foreground">{event.insuredName ?? "--"}</p>
      <p>COI {event.id.slice(0, 8)}</p>
      <p>{originLabel}</p>
      <p>Duration: {formatDuration(event.durationSeconds)}</p>
      <p>Policy sync: {event.syncTriggered ? "Yes" : "No"}</p>
      <p className="text-muted-foreground">
        {new Date(event.sentAt).toLocaleString()}
      </p>
    </div>
  );
}

// CSR-1349 — send-duration aggregates (avg/p50/p90/p95) per send origin.
// Stable layout: all four groups and all four stats always render; null
// aggregates show "--" so the page shape never changes with data
// availability.
export function CoiSendTimesTab() {
  const [windowDays, setWindowDays] = useState(30);
  const { data } = useCoiSendMetrics(windowDays);
  const { data: eventsData } = useCoiSendEvents(windowDays);

  const eventSeries = useMemo(() => {
    const points: CoiSendEventPoint[] = (eventsData?.events ?? []).map(
      (event) => ({ ...event, sentAtMs: new Date(event.sentAt).getTime() }),
    );
    return COI_SEND_EVENT_SERIES.map((series) => ({
      ...series,
      points: points.filter((point) => point.sendOrigin === series.key),
    }));
  }, [eventsData]);
  const eventCount = eventsData?.events?.length ?? 0;

  return (
    <div className="space-y-6" data-testid="coi-send-times-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Timer className="h-4 w-4" />
            COI Send Times
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Time from send trigger to delivered email, by send origin.
          </p>
        </div>
        <Select
          value={String(windowDays)}
          onValueChange={(v) => setWindowDays(Number(v))}
        >
          <SelectTrigger
            className="w-[160px] bg-white"
            data-testid="select-coi-send-window"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COI_SEND_TIME_WINDOWS.map((days) => (
              <SelectItem key={days} value={String(days)}>
                Last {days} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {COI_SEND_GROUPS.map(({ key, label }) => {
        const group = data?.groups?.[key];
        return (
          <div
            key={key}
            className="rounded-xl border bg-card p-4 space-y-3"
            data-testid={`coi-send-group-${key}`}
          >
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold">{label}</h3>
              <p className="text-xs text-muted-foreground">
                based on {group?.count ?? 0} sends
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Average" value={formatDuration(group?.avgSeconds)} />
              <StatCard
                label="Median (p50)"
                value={formatDuration(group?.p50Seconds)}
              />
              <StatCard label="p90" value={formatDuration(group?.p90Seconds)} />
              <StatCard label="p95" value={formatDuration(group?.p95Seconds)} />
            </div>
          </div>
        );
      })}

      <div
        className="rounded-xl border bg-card p-4 space-y-3"
        data-testid="coi-send-events-chart"
      >
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold">Send Events</h3>
          <p className="text-xs text-muted-foreground">
            {eventsData?.truncated
              ? `showing the ${eventsData.limit} most recent sends`
              : `based on ${eventCount} sends`}
          </p>
        </div>
        <div className="h-[300px]">
          {eventCount > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  type="number"
                  dataKey="sentAtMs"
                  name="Sent"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={formatSentAtTick}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="number"
                  dataKey="durationSeconds"
                  name="Duration"
                  tickFormatter={(value: number) => formatDuration(value)}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  tickLine={false}
                  axisLine={false}
                  width={60}
                />
                <RechartsTooltip
                  content={<CoiSendEventTooltip />}
                  cursor={{ strokeDasharray: "3 3" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {eventSeries.map(({ key, label, color, points }) => (
                  <Scatter key={key} name={label} data={points} fill={color} />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div
              className="h-full flex items-center justify-center text-sm text-muted-foreground"
              data-testid="coi-send-events-empty"
            >
              No COI sends in this window yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type Tab =
  | "logs"
  | "coi-requests"
  | "intercom-events"
  | "feature-flags"
  | "insureds-without-policies"
  | "coi-send-times"
  | "stale-sync";

export default function SystemSettings() {
  const [activeTab, setActiveTab] = useState<Tab>("coi-requests");
  const staleSyncEnabled = useFeatureFlag(FLAG_STALE_SYNC_VIEW);
  const aorEnabled = useFeatureFlag(FLAG_AOR_EXPERIENCE);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold font-heading text-foreground tracking-tight">
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            System activity and admin tools.
          </p>
        </div>

        <div className="border-b border-border">
          <nav className="flex gap-6">
            <button
              onClick={() => setActiveTab("logs")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === "logs"
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <ScrollText className="h-4 w-4" />
              Logs
            </button>
            <button
              onClick={() => setActiveTab("coi-requests")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === "coi-requests"
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Inbox className="h-4 w-4" />
              COI Requests
            </button>
            <button
              onClick={() => setActiveTab("intercom-events")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === "intercom-events"
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Webhook className="h-4 w-4" />
              Intercom Events
            </button>
            <button
              onClick={() => setActiveTab("feature-flags")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === "feature-flags"
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Flag className="h-4 w-4" />
              Feature Flags
            </button>
            <button
              onClick={() => setActiveTab("insureds-without-policies")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === "insureds-without-policies"
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              data-testid="tab-insureds-without-policies"
            >
              <UserX className="h-4 w-4" />
              Insureds without policies
            </button>
            <button
              onClick={() => setActiveTab("coi-send-times")}
              className={cn(
                "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                activeTab === "coi-send-times"
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              data-testid="tab-coi-send-times"
            >
              <Timer className="h-4 w-4" />
              COI Send Times
            </button>
            {staleSyncEnabled && (
              <button
                onClick={() => setActiveTab("stale-sync")}
                className={cn(
                  "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer",
                  activeTab === "stale-sync"
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
                data-testid="tab-stale-sync"
              >
                <AlarmClockOff className="h-4 w-4" />
                Stale Sync
              </button>
            )}
          </nav>
        </div>

        {activeTab === "logs" && (
          <div className="py-12 text-center text-muted-foreground">
            Activity logs will appear here.
          </div>
        )}

        {activeTab === "coi-requests" && <COIRequestsTab />}
        {activeTab === "intercom-events" && <IntercomEventsTab />}
        {activeTab === "feature-flags" && <FeatureFlagsTab />}
        {activeTab === "insureds-without-policies" && <InsuredsWithoutPoliciesTab />}
        {activeTab === "coi-send-times" && <CoiSendTimesTab />}
        {activeTab === "stale-sync" && staleSyncEnabled && (
          <StaleSyncTab showAor={aorEnabled} />
        )}
      </div>
    </Layout>
  );
}

// ============================================================
// Feature Flags Tab
// ============================================================

function FeatureFlagsTab() {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<FeatureFlag | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingFlag, setDeletingFlag] = useState<FeatureFlag | null>(null);

  const { data: flags, isLoading } = useFeatureFlags(search);
  const updateMutation = useUpdateFeatureFlag();
  const { toast } = useToast();

  async function handleToggleActive(flag: FeatureFlag, nextActive: boolean) {
    try {
      await updateMutation.mutateAsync({
        id: flag.id,
        data: { isActive: nextActive },
      });
    } catch (err) {
      toast({
        title: "Failed to update feature flag",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Flag className="h-4 w-4" />
            Feature Flags
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Admin-managed flags read at runtime via{" "}
            <code className="text-xs">isFeatureFlagActive(name)</code>. Changes
            take effect immediately.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Feature Flag
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-lg bg-white"
          />
        </div>
      </div>

      <Card className="border-border shadow-none overflow-hidden rounded-xl">
        <div className="overflow-auto">
          <table className={TABLE_CLASS}>
            <thead className={THEAD_CLASS}>
              <tr>
                <th className={TH_CLASS}>Name</th>
                <th className={TH_CLASS}>Description</th>
                <th className={TH_CLASS}>Status</th>
                <th className={TH_CLASS}>Updated</th>
                <th className={cn(TH_CLASS, "text-right")}>Actions</th>
              </tr>
            </thead>
            <tbody className={TBODY_CLASS}>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : !flags || flags.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                    {search.trim()
                      ? "No feature flags match your search."
                      : "No feature flags yet. Click \"Add Feature Flag\" to create one."}
                  </td>
                </tr>
              ) : (
                flags.map((flag) => (
                  <tr key={flag.id} className={TR_CLASS}>
                    <td className={TD_CLASS}>
                      <span className="font-mono text-sm font-medium">{flag.name}</span>
                    </td>
                    <td className={TD_CLASS}>
                      <span className="text-sm text-muted-foreground">
                        {flag.description || "—"}
                      </span>
                    </td>
                    <td className={TD_CLASS}>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={flag.isActive}
                          onCheckedChange={(checked) => handleToggleActive(flag, checked)}
                          disabled={updateMutation.isPending}
                        />
                        <Badge
                          variant={flag.isActive ? "default" : "secondary"}
                          className={cn(
                            flag.isActive
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {flag.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </td>
                    <td className={cn(TD_CLASS, "whitespace-nowrap")}>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(flag.updatedAt)}
                      </span>
                    </td>
                    <td className={cn(TD_CLASS, "text-right")}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(flag)}
                          aria-label="Edit feature flag"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingFlag(flag)}
                          aria-label="Delete feature flag"
                          className="text-rose-500 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <FeatureFlagFormDialog
        open={creating}
        onOpenChange={setCreating}
        flag={null}
      />
      <FeatureFlagFormDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        flag={editing}
      />
      <DeleteFeatureFlagDialog
        flag={deletingFlag}
        onOpenChange={(open) => {
          if (!open) setDeletingFlag(null);
        }}
      />
    </div>
  );
}

function FeatureFlagFormDialog({
  open,
  onOpenChange,
  flag,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flag: FeatureFlag | null;
}) {
  const isEdit = flag !== null;
  const [name, setName] = useState(flag?.name ?? "");
  const [description, setDescription] = useState(flag?.description ?? "");
  const [isActive, setIsActive] = useState(flag?.isActive ?? false);
  const createMutation = useCreateFeatureFlag();
  const updateMutation = useUpdateFeatureFlag();
  const { toast } = useToast();

  // Reset form state whenever the dialog opens for a different flag.
  useEffect(() => {
    if (open) {
      setName(flag?.name ?? "");
      setDescription(flag?.description ?? "");
      setIsActive(flag?.isActive ?? false);
    }
  }, [open, flag]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast({
        title: "Name is required",
        variant: "destructive",
      });
      return;
    }
    try {
      if (isEdit && flag) {
        await updateMutation.mutateAsync({
          id: flag.id,
          data: {
            name: trimmed,
            description: description.trim() || null,
            isActive,
          },
        });
        toast({ title: "Feature flag updated" });
      } else {
        await createMutation.mutateAsync({
          name: trimmed,
          description: description.trim() || null,
          isActive,
        });
        toast({ title: "Feature flag created" });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        title: isEdit ? "Failed to update feature flag" : "Failed to create feature flag",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit Feature Flag" : "Add Feature Flag"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the flag's name, description, or active state."
                : "Register a new admin-managed feature flag."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ff-name">Name</Label>
              <Input
                id="ff-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. enable_new_coi_pipeline"
                disabled={isPending}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Stable identifier read by{" "}
                <code className="text-xs">isFeatureFlagActive(name)</code>.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ff-description">Description</Label>
              <Textarea
                id="ff-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this flag gate?"
                rows={3}
                disabled={isPending}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="ff-active" className="text-sm font-medium">
                  Active
                </Label>
                <p className="text-xs text-muted-foreground">
                  Off by default. Inactive flags resolve to false.
                </p>
              </div>
              <Switch
                id="ff-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving…" : isEdit ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteFeatureFlagDialog({
  flag,
  onOpenChange,
}: {
  flag: FeatureFlag | null;
  onOpenChange: (open: boolean) => void;
}) {
  const deleteMutation = useDeleteFeatureFlag();
  const { toast } = useToast();

  async function handleDelete() {
    if (!flag) return;
    try {
      await deleteMutation.mutateAsync(flag.id);
      toast({ title: "Feature flag deleted" });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Failed to delete feature flag",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }

  return (
    <AlertDialog open={flag !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete feature flag?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete <strong>{flag?.name}</strong>. Any code
            calling <code className="text-xs">isFeatureFlagActive(&quot;{flag?.name}&quot;)</code>{" "}
            will receive <code className="text-xs">false</code>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="bg-rose-500 text-white hover:bg-rose-600"
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================
// Insureds Without Policies Tab (CSR-1157)
// ============================================================

const INSUREDS_WITHOUT_POLICIES_PAGE_SIZE = 20;

function InsuredsWithoutPoliciesTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isPlaceholderData } = useInsuredsWithoutPolicies({
    page,
    limit: INSUREDS_WITHOUT_POLICIES_PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / INSUREDS_WITHOUT_POLICIES_PAGE_SIZE));
  const showFooter = totalCount > INSUREDS_WITHOUT_POLICIES_PAGE_SIZE;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <UserX className="h-4 w-4" />
          Insureds without policies
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Insureds with no linked policies. Visibility only.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Insureds without policies" value={totalCount} />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className={TABLE_CLASS}>
            <thead className={THEAD_CLASS}>
              <tr>
                <th className={TH_CLASS}>Name</th>
                <th className={TH_CLASS}>Source(s)</th>
                <th className={TH_CLASS}>Created at</th>
              </tr>
            </thead>
            <tbody className={TBODY_CLASS}>
              {isLoading && (
                <tr>
                  <td className={cn(TD_CLASS, "text-center text-muted-foreground")} colSpan={3}>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && isError && (
                <tr>
                  <td className={cn(TD_CLASS, "text-center text-rose-600")} colSpan={3}>
                    Failed to load insureds without policies.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && items.length === 0 && (
                <tr>
                  <td className={cn(TD_CLASS, "text-center text-muted-foreground")} colSpan={3}>
                    No insureds without policies.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !isError &&
                items.map((row) => (
                  <tr
                    key={row.id}
                    className={TR_CLASS}
                    data-testid={`row-insured-without-policies-${row.id}`}
                  >
                    <td className={TD_CLASS}>{row.name ?? "--"}</td>
                    <td className={TD_CLASS}>
                      <SourceChips sources={row.sources} />
                    </td>
                    <td className={TD_CLASS}>
                      {row.createdAt ? formatDate(row.createdAt) : "--"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {showFooter && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              {((page - 1) * INSUREDS_WITHOUT_POLICIES_PAGE_SIZE + 1).toLocaleString()}&ndash;
              {Math.min(page * INSUREDS_WITHOUT_POLICIES_PAGE_SIZE, totalCount).toLocaleString()}{" "}
              of {totalCount.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isPlaceholderData}
                data-testid="button-prev-page"
              >
                Previous
              </Button>
              <span className="text-sm px-3">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isPlaceholderData}
                data-testid="button-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// CSR-1157 — `sources` arrives from DC as a comma-joined string (e.g.
// "nowcerts,fmcsa,slack"). Split + trim + render each piece as a small chip
// for a glanceable view. Falls back to "--" when the column is null/empty
// (layout-stability rule — never collapse the cell).
function SourceChips({ sources }: { sources: string | null }) {
  if (!sources) return <span className="text-muted-foreground">--</span>;
  const pieces = sources
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (pieces.length === 0) return <span className="text-muted-foreground">--</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {pieces.map((p) => (
        <Badge
          key={p}
          variant="secondary"
          className="bg-muted text-foreground/80 hover:bg-muted text-xs font-normal"
        >
          {p}
        </Badge>
      ))}
    </div>
  );
}
